<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Flip } from 'gsap/Flip'

const { scrollToTarget } = useSmoothScroll()

/* Echte Anker-Links (crawlbar, Rechtsklick/Copy-Link funktioniert); der
   Klick wird abgefangen und weich per ScrollTo gefahren. */
const links = [
  { label: 'How it works', target: '#funktionsweise' },
  { label: 'Modules', target: '#module' },
  { label: 'Integrations', target: '#integrationen' },
  { label: 'Connectors', target: '#connectors' },
  { label: 'Plans', target: '#plans' }
]

const mobileOpen = ref(false)
const headerRef = ref<HTMLElement | null>(null)
const pillRef = ref<HTMLElement | null>(null)
let scrollState: ScrollTrigger | undefined

/* Mit Hero-Logo auf der Seite taktet der Logo-Dock den Morph (erst Logo in
   die Nav, dann Nav klein) — siehe useNavDock */
const navDock = useNavDock()
let releaseNav: (() => void) | undefined
let compact = false

/* Zwei Nav-Zustände: ganz oben hängt die Nav als eckiger Balken in voller
   Gerüst-Breite (Rails) im Gestell, beim Scrollen löst sie sich und zieht
   sich zur kompakten Pill zusammen. Die Breite (auto ↔ Rail-Breite) ist
   nicht CSS-animierbar — GSAP Flip misst beide Layouts und tweent dazwischen;
   Flexbox verteilt die Kinder dabei pro Frame von selbst. */
function applyNavState(active: boolean, animate = true) {
  const header = headerRef.value
  const pill = pillRef.value
  if (!header || !pill || active === compact) return
  compact = active

  const animatable
    = animate
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && window.matchMedia('(min-width: 900px)').matches

  if (!animatable) {
    header.classList.toggle('is-scrolled', active)
    return
  }

  // Innen-Gruppen mitmessen: Flip federt so auch deren Versatz ab
  const targets = [
    pill,
    ...Array.from(pill.querySelectorAll<HTMLElement>(
      '.site-nav-brand, .site-nav-links, .site-nav-actions'
    ))
  ]
  const state = Flip.getState(targets)
  header.classList.toggle('is-scrolled', active)
  Flip.from(state, {
    duration: NAV_MORPH_DURATION,
    ease: 'power3.inOut',
    overwrite: true,
    nested: true // Logo liegt in der Brand-Gruppe — keine Doppel-Transforms
  })
}

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger, Flip)
  /* progress statt isActive: ganz unten (progress 1) gilt ein Trigger als
     inaktiv — die Nav soll dort aber kompakt bleiben */
  scrollState = ScrollTrigger.create({
    start: 70,
    end: 'max',
    onUpdate: (self) => {
      if (!navDock.isClaimed()) applyNavState(self.progress > 0)
    }
  })
  releaseNav = navDock.provideNav({
    setCompact: applyNavState,
    isCompact: () => compact,
    sync: () => applyNavState((scrollState?.progress ?? 0) > 0, false)
  })
})

onBeforeUnmount(() => {
  scrollState?.kill()
  releaseNav?.()
})

const route = useRoute()

/* Die Anker liegen auf der Startseite — von Unterseiten (Impressum) dorthin
   navigieren, auf der Startseite weich scrollen */
function go(target: string) {
  mobileOpen.value = false
  if (route.path !== '/') return navigateTo(target === 'body' ? '/' : `/${target}`)
  scrollToTarget(target)
}
</script>

<template>
  <header ref="headerRef" class="site-nav" @keydown.esc="mobileOpen = false">
    <nav ref="pillRef" class="site-nav-pill" aria-label="Main">
      <!-- Eingehakt: Plus-Marks an den unteren Ecken, exakt auf den Rails -->
      <span class="cell-plus cell-plus-bl site-nav-hook" aria-hidden="true" />
      <span class="cell-plus cell-plus-br site-nav-hook" aria-hidden="true" />
      <!-- Accessible Name kommt aus dem sichtbaren Text („Clarify"),
           der Zweck steht im title — kein aria-label, das vom Sichtbaren abweicht -->
      <a href="/" class="site-nav-brand" title="Back to top" @click.prevent="go('body')">
        <img src="/brand/clarify-mark-white.svg" alt="" width="32" height="32" class="site-nav-logo">
        <span class="site-nav-name">Clarify</span>
      </a>

      <div class="site-nav-links">
        <a v-for="l in links" :key="l.target" :href="`/${l.target}`" class="nav-link site-nav-link" @click.prevent="go(l.target)">
          {{ l.label }}
        </a>
      </div>

      <div class="site-nav-actions">
        <UButton
          icon="i-lucide-book-open"
          color="neutral"
          variant="ghost"
          size="sm"
          to="https://docs.clarify.bot"
          target="_blank"
          aria-label="Documentation"
          title="Docs"
          class="site-nav-docs"
        />
        <UButton
          label="Dashboard"
          color="neutral"
          variant="ghost"
          size="sm"
          to="https://app.clarify.bot"
          target="_blank"
          class="hidden sm:inline-flex"
        />
        <UButton
          label="Start for free"
          size="sm"
          class="site-nav-cta"
          to="https://app.clarify.bot"
          target="_blank"
        />
        <button
          class="site-nav-burger"
          :aria-label="mobileOpen ? 'Close menu' : 'Open menu'"
          :aria-expanded="mobileOpen"
          aria-controls="site-nav-menu"
          @click="mobileOpen = !mobileOpen"
        >
          <UIcon :name="mobileOpen ? 'i-lucide-x' : 'i-lucide-menu'" class="size-5" />
        </button>
      </div>
    </nav>

    <Transition name="menu">
      <div v-if="mobileOpen" id="site-nav-menu" class="site-nav-menu">
        <a v-for="l in links" :key="l.target" :href="`/${l.target}`" class="site-nav-menu-link" @click.prevent="go(l.target)">
          {{ l.label }}
        </a>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.site-nav {
  position: fixed;
  inset-inline: 0;
  top: 22px;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  transition: top var(--dur-slow) var(--ease-out);
}

.site-nav.is-scrolled {
  top: 12px;
}

/* Grundform: konstante Innen-Geometrie (Gap/Padding identisch in beiden
   Zuständen — Flip animiert NUR die Breite, Flexbox verteilt die Kinder).
   Kompakt = Pill (max-content, rund); oben = Balken in Rail-Breite, eckig. */
.site-nav-pill {
  position: relative;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  width: max-content;
  max-width: 92vw;
  padding: 12px 16px 12px 22px;
  border-radius: var(--radius-pill);
  background: var(--nb-glass);
  border: 1px solid var(--nb-hairline);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: none;
  transition:
    border-radius var(--dur-slow) var(--ease-out),
    background var(--dur-slow) var(--ease-out),
    border-color var(--dur-slow) var(--ease-out),
    box-shadow var(--dur-slow) var(--ease-out);
}

.site-nav.is-scrolled .site-nav-pill {
  background: var(--nb-menu-bg);
  border-color: var(--nb-hairline-strong);
  box-shadow: var(--shadow-float);
}

/* Die Einhak-Marks: nur sichtbar, solange der Balken im Gestell hängt */
.site-nav-hook {
  opacity: 0;
  transition: opacity var(--dur-slow) var(--ease-out);
}

.site-nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.site-nav-logo {
  width: 32px;
  height: 32px;
  display: block;
  flex-shrink: 0;
  /* Der Slot fährt auf, wenn das Hero-Logo andockt (Logo-Dock, main.css) —
     der Schriftzug weicht dabei weich nach rechts aus */
  transition:
    width var(--dur-slow) var(--ease-out),
    margin var(--dur-slow) var(--ease-out);
}

.site-nav-name {
  font-weight: 600;
  font-size: 1.08rem;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
}

/* ── Oben eingehakt: voller Gerüst-Balken (nur Desktop mit Links) ────────── */
@media (min-width: 900px) {
  .site-nav:not(.is-scrolled) .site-nav-pill {
    width: var(--nb-shell);
    border-radius: var(--radius-card); /* eckig = gerüst-gebunden */
  }

  .site-nav:not(.is-scrolled) .site-nav-hook {
    opacity: 1;
  }
}

.site-nav-links {
  display: none;
  align-items: center;
  gap: 24px;
  transition: gap var(--dur-slow) var(--ease-out);
}

.site-nav-link {
  font-size: 0.92rem;
  cursor: pointer;
  padding-bottom: 2px;
  transition:
    font-size var(--dur-slow) var(--ease-out),
    background-size var(--dur) var(--ease-out),
    color var(--dur) var(--ease-out);
}

.site-nav-actions {
  display: flex;
  align-items: center;
  gap: 9px;
  transition: gap var(--dur-slow) var(--ease-out);
}

.site-nav-cta {
  background: var(--nb-accent);
  color: #fff;
  border-radius: var(--radius-pill);
  transition:
    background var(--dur) var(--ease-out),
    transform var(--dur) var(--ease-out);
}

.site-nav-cta:hover {
  background: var(--nb-accent-hover);
  transform: translateY(-1px);
}

.site-nav-burger {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-pill);
  color: var(--nb-ink);
  cursor: pointer;
}

.site-nav-menu {
  pointer-events: auto;
  margin-top: 10px;
  width: min(420px, 92vw);
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 2px;
  border-radius: var(--radius-lg);
  background: var(--nb-menu-bg);
  border: 1px solid var(--nb-hairline);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: var(--shadow-float);
}

.site-nav-menu-link {
  text-align: left;
  padding: 12px 14px;
  border-radius: var(--radius-md);
  color: var(--nb-ink-muted);
  font-size: 1rem;
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    color var(--dur-fast) var(--ease-out);
}

.site-nav-menu-link:hover {
  background: var(--nb-accent-tint);
  color: var(--nb-ink);
}

.menu-enter-active,
.menu-leave-active {
  transition:
    opacity var(--dur) var(--ease-out),
    transform var(--dur) var(--ease-out);
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (min-width: 900px) {
  .site-nav-links {
    display: flex;
  }

  .site-nav-burger {
    display: none;
  }
}
/* Phones: Brand + CTA + Burger müssen in die Pill passen — der Docs-Button
   weicht (er steckt im Footer). */
@media (max-width: 639.98px) {
  .site-nav-actions :deep(.site-nav-docs) {
    display: none;
  }

  .site-nav-pill {
    padding-left: 14px;
    padding-right: 12px;
  }
}
</style>
